import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

interface PendingLeave { proceed: () => void }

interface DirtyApi {
  dirty: boolean;
  setDirty: (value: boolean) => void;
  setSaver: (saver: (() => Promise<void>) | null) => void;
  requestLeave: (proceed: () => void) => void;
  pending: PendingLeave | null;
  stay: () => void;
  discard: () => void;
  saveAndLeave: () => Promise<void>;
}

const DirtyContext = createContext<DirtyApi | null>(null);

export function DirtyProvider({ children }: { children: ReactNode }) {
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState<PendingLeave | null>(null);
  const [saver, setSaverState] = useState<(() => Promise<void>) | null>(null);
  const setSaver = useCallback((next: (() => Promise<void>) | null) => setSaverState(() => next), []);
  const requestLeave = useCallback((proceed: () => void) => {
    if (!dirty) {
      proceed();
      return;
    }
    setPending({ proceed });
  }, [dirty]);
  const stay = useCallback(() => setPending(null), []);
  const discard = useCallback(() => {
    const next = pending;
    setDirty(false);
    setPending(null);
    next?.proceed();
  }, [pending]);
  const saveAndLeave = useCallback(async () => {
    if (saver) await saver();
    const next = pending;
    setDirty(false);
    setPending(null);
    next?.proceed();
  }, [pending, saver]);
  const value = useMemo(
    () => ({ dirty, setDirty, setSaver, requestLeave, pending, stay, discard, saveAndLeave }),
    [dirty, setSaver, requestLeave, pending, stay, discard, saveAndLeave],
  );
  return <DirtyContext.Provider value={value}>{children}</DirtyContext.Provider>;
}

export function loadedRecordKey(routeId: string | undefined, loadedId: string | undefined): string {
  if (!routeId) return '';
  if (routeId === 'new') return 'new';
  return loadedId === routeId ? routeId : '';
}

export function useEditGuard(recordKey: string, snapshot: string, save: () => Promise<unknown>, opts?: { silent?: boolean; onDirty?: (dirty: boolean) => void }): () => void {
  const dirtyApi = useDirty();
  const base = useRef<{ key: string; snapshot: string } | null>(null);
  const saveRef = useRef(save);
  const snapshotRef = useRef(snapshot);
  const recordKeyRef = useRef(recordKey);
  const onDirtyRef = useRef(opts?.onDirty);
  const silent = Boolean(opts?.silent);
  const setDirty = useRef(dirtyApi.setDirty);
  const setSaver = useRef(dirtyApi.setSaver);
  saveRef.current = save;
  snapshotRef.current = snapshot;
  recordKeyRef.current = recordKey;
  onDirtyRef.current = opts?.onDirty;
  setDirty.current = dirtyApi.setDirty;
  setSaver.current = dirtyApi.setSaver;
  const markClean = useCallback(() => {
    if (!recordKeyRef.current) return;
    base.current = { key: recordKeyRef.current, snapshot: snapshotRef.current };
    onDirtyRef.current?.(false);
    if (silent) return;
    setDirty.current(false);
    setSaver.current(null);
  }, [silent]);
  useEffect(() => {
    function publish(changed: boolean) {
      onDirtyRef.current?.(changed);
      if (silent) return;
      setDirty.current(changed);
      setSaver.current(changed ? async () => { await saveRef.current(); } : null);
    }
    if (!recordKey) {
      base.current = null;
      publish(false);
      return;
    }
    if (!base.current || base.current.key !== recordKey) {
      base.current = { key: recordKey, snapshot };
      publish(false);
      return;
    }
    publish(snapshot !== base.current.snapshot);
    return () => {
      if (!silent) setSaver.current(null);
    };
  }, [recordKey, snapshot, silent]);
  return markClean;
}

export function useDirty(): DirtyApi {
  const value = useContext(DirtyContext);
  if (!value) {
    return {
      dirty: false,
      setDirty: () => undefined,
      setSaver: () => undefined,
      requestLeave: (proceed) => proceed(),
      pending: null,
      stay: () => undefined,
      discard: () => undefined,
      saveAndLeave: async () => undefined,
    };
  }
  return value;
}
